import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { artigos, errosConhecidos } from '@/portal/portal.api';

/**
 * Soluções — a tela de deflexão.
 *
 * Duas fontes lado a lado, porque respondem a perguntas diferentes: o artigo
 * ensina a fazer, o erro conhecido diz o que fazer enquanto a Aptum conserta.
 * Misturá-los numa lista só apagaria essa diferença justo para quem mais
 * precisa dela — quem está parado agora.
 */
export function BasePage() {
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');

  const conhecimento = useQuery({
    queryKey: ['portal', 'artigos', termo],
    queryFn: () => artigos(termo || undefined),
  });
  const erros = useQuery({
    queryKey: ['portal', 'erros', termo],
    queryFn: () => errosConhecidos(termo || undefined),
  });

  return (
    <div className="space-y-4">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setTermo(busca.trim());
        }}
      >
        <input
          type="search"
          value={busca}
          placeholder="O que você está tentando resolver?"
          onChange={(e) => setBusca(e.target.value)}
          className="h-10 flex-1 rounded-md border border-border bg-background px-3 text-sm"
        />
        <button
          type="submit"
          className="h-10 rounded-md bg-primary px-4 text-sm text-primary-foreground"
        >
          Buscar
        </button>
      </form>

      <Card>
        <CardHeader title="Artigos" description="Como fazer, passo a passo." />
        <ul className="divide-y divide-border">
          {conhecimento.data?.items.map((item) => (
            <li key={item.id}>
              <Link
                to="/base/$id"
                params={{ id: item.id }}
                className="block px-5 py-4 hover:bg-accent/50 transition-colors"
              >
                <p className="font-mono text-xs text-muted-foreground">{item.codigo}</p>
                <p className="font-medium text-foreground">{item.titulo}</p>
                <p className="mt-1 text-sm text-muted-foreground">{item.resumo}</p>
              </Link>
            </li>
          ))}
        </ul>
        {conhecimento.data?.items.length === 0 ? (
          <div className="px-5 py-4">
            <EmptyState title="Nenhum artigo publicado para a sua empresa ainda." />
          </div>
        ) : null}
      </Card>

      <Card>
        <CardHeader
          title="Problemas conhecidos"
          description="Falhas que a Aptum já identificou, com o que fazer enquanto isso."
        />
        <ul className="divide-y divide-border">
          {erros.data?.items.map((erro) => (
            <li key={erro.id} className="px-5 py-4">
              <p className="font-mono text-xs text-muted-foreground">{erro.codigo}</p>
              <p className="font-medium">{erro.titulo}</p>
              <p className="mt-1 text-sm text-muted-foreground">{erro.sintomas}</p>
              <p className="mt-2 text-sm">
                <span className="font-medium">Enquanto isso: </span>
                {erro.contorno}
              </p>
            </li>
          ))}
        </ul>
        {erros.data?.items.length === 0 ? (
          <div className="px-5 py-4">
            <EmptyState title="Nenhum problema conhecido afeta os seus serviços." />
          </div>
        ) : null}
      </Card>
    </div>
  );
}
